/**
 * Desktop Sidebar Navigation
 * Persistent sidebar for desktop web with hover effects and icons
 */
import React, { useState, useContext } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  Platform,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { AuthContext } from '../../../Context/AuthContext';
import { DesktopTheme } from '../theme/desktopTheme';
import ESG_logoImage from '../../../assets/ESG_logo.png';

// Navigation items configuration
const NAV_ITEMS = [
  {
    name: 'ESG Pillars',
    label: 'Dashboard',
    icon: '📊',
  },
  {
    name: 'Contributors',
    label: 'Contributors',
    icon: '👥',
  },
  {
    name: 'Dept Performance',
    label: 'Department Scores',
    icon: '🏢',
  },
  {
    name: 'Report',
    label: 'All Reports',
    icon: '📋',
  },
  {
    name: 'Improvement Report',
    label: 'Improvement',
    icon: '📈',
  },
  {
    name: 'Remaining Topics',
    label: 'Topic Assignment',
    icon: '📝',
  },
];

const SECONDARY_NAV = [
  {
    name: 'Feedback & Support',
    label: 'Feedback',
    icon: '💬',
  },
];

const SidebarItem = ({ item, isActive, onPress, isCollapsed }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <TouchableOpacity
      style={[
        styles.navItem,
        isActive && styles.navItemActive,
        isHovered && !isActive && styles.navItemHover,
      ]}
      onPress={onPress}
      onMouseEnter={Platform.OS === 'web' ? handleMouseEnter : undefined}
      onMouseLeave={Platform.OS === 'web' ? handleMouseLeave : undefined}
      activeOpacity={0.7}
    >
      <View style={styles.navItemContent}>
        <Text style={styles.navIcon}>{item.icon}</Text>
        {!isCollapsed && (
          <Text
            style={[
              styles.navLabel,
              isActive && styles.navLabelActive,
            ]}
            numberOfLines={1}
          >
            {item.label}
          </Text>
        )}
      </View>
      {isActive && <View style={styles.activeIndicator} />}
    </TouchableOpacity>
  );
};

const Sidebar = ({ isCollapsed = false, currentRoute }) => {
  const navigation = useNavigation();
  const { companyName, logoutCompany } = useContext(AuthContext);
  const [isLogoutHovered, setIsLogoutHovered] = useState(false);

  const handleNavigation = (screenName) => {
    navigation.navigate(screenName);
  };

  const handleLogout = async () => {
    await logoutCompany();
    navigation.reset({
      index: 0,
      routes: [{ name: 'CompanyLoginScreen' }],
    });
  };

  return (
    <View style={[styles.sidebar, isCollapsed && styles.sidebarCollapsed]}>
      {/* Logo Section */}
      <View style={styles.logoSection}>
        <Image
          source={ESG_logoImage}
          style={[styles.logo, isCollapsed && styles.logoCollapsed]}
          resizeMode="contain"
        />
        {!isCollapsed && (
          <View style={styles.companyInfo}>
            <Text style={styles.appName}>ESG Dashboard</Text>
            <Text style={styles.companyName} numberOfLines={1}>
              {companyName || 'Company'}
            </Text>
          </View>
        )}
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Main Navigation */}
      <ScrollView
        style={styles.navSection}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.navGroup}>
          {!isCollapsed && <Text style={styles.navGroupLabel}>MAIN MENU</Text>}
          {NAV_ITEMS.map((item) => (
            <SidebarItem
              key={item.name}
              item={item}
              isActive={currentRoute === item.name}
              onPress={() => handleNavigation(item.name)}
              isCollapsed={isCollapsed}
            />
          ))}
        </View>

        <View style={[styles.navGroup, { marginTop: 24 }]}>
          {!isCollapsed && <Text style={styles.navGroupLabel}>SUPPORT</Text>}
          {SECONDARY_NAV.map((item) => (
            <SidebarItem
              key={item.name}
              item={item}
              isActive={currentRoute === item.name}
              onPress={() => handleNavigation(item.name)}
              isCollapsed={isCollapsed}
            />
          ))}
        </View>
      </ScrollView>

      {/* Logout Button */}
      <View style={styles.logoutSection}>
        <View style={styles.divider} />
        <TouchableOpacity
          style={[
            styles.logoutButton,
            isLogoutHovered && styles.logoutButtonHover,
          ]}
          onPress={handleLogout}
          onMouseEnter={Platform.OS === 'web' ? () => setIsLogoutHovered(true) : undefined}
          onMouseLeave={Platform.OS === 'web' ? () => setIsLogoutHovered(false) : undefined}
          activeOpacity={0.7}
        >
          <Text style={styles.logoutIcon}>🚪</Text>
          {!isCollapsed && <Text style={styles.logoutText}>Logout</Text>}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const { colors, typography, spacing, layout, borderRadius } = DesktopTheme;

const styles = StyleSheet.create({
  sidebar: {
    width: layout.sidebarWidth,
    height: '100%',
    backgroundColor: colors.sidebarBg,
    paddingVertical: spacing.lg,
    flexDirection: 'column',
  },
  sidebarCollapsed: {
    width: layout.sidebarCollapsedWidth,
  },

  // Logo Section
  logoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  logo: {
    width: 48,
    height: 48,
    borderRadius: 12,
  },
  logoCollapsed: {
    width: 40,
    height: 40,
  },
  companyInfo: {
    marginLeft: spacing.md,
    flex: 1,
  },
  appName: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: colors.textOnDark,
    marginBottom: 2,
  },
  companyName: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textOnDarkMuted,
  },

  // Divider
  divider: {
    height: 1,
    backgroundColor: colors.borderDark,
    marginHorizontal: spacing.lg,
    marginVertical: spacing.md,
  },

  // Navigation
  navSection: {
    flex: 1,
  },
  navGroup: {
    paddingHorizontal: spacing.md,
  },
  navGroupLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.tiny,
    color: colors.textOnDarkMuted,
    letterSpacing: 1,
    marginBottom: spacing.sm,
    marginLeft: spacing.sm,
  },

  // Nav Item
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm + 4,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.xs,
    position: 'relative',
  },
  navItemHover: {
    backgroundColor: colors.sidebarHover,
  },
  navItemActive: {
    backgroundColor: colors.sidebarActive,
  },
  navItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  navIcon: {
    fontSize: 18,
    width: 28,
    textAlign: 'center',
  },
  navLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textOnDarkMuted,
    marginLeft: spacing.sm,
  },
  navLabelActive: {
    color: colors.textOnDark,
    fontFamily: typography.fontFamily.heading,
  },
  activeIndicator: {
    position: 'absolute',
    left: 0,
    top: '25%',
    bottom: '25%',
    width: 3,
    backgroundColor: colors.primary,
    borderRadius: 2,
  },

  // Logout
  logoutSection: {
    marginTop: 'auto',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginHorizontal: spacing.md,
    borderRadius: borderRadius.md,
  },
  logoutButtonHover: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  },
  logoutIcon: {
    fontSize: 18,
    width: 28,
    textAlign: 'center',
  },
  logoutText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.error,
    marginLeft: spacing.sm,
  },
});

export default Sidebar;
